/**
 * Schedule anomaly review.
 *
 * This is the half of the feature that makes the other half worth having. The
 * collector can spot that one machine at a client runs Mon–Fri while fifteen
 * others run daily, but a finding nobody can close gets re-reported every
 * fifteen minutes until people stop reading it — at which point it is worse
 * than silence, because it has trained someone to ignore a channel that will
 * one day carry something real.
 *
 * Two buttons, and they mean genuinely different things:
 *
 *   Intentional  This machine is allowed to be different. Stop asking. The
 *                record stays — that a human reviewed it IS the useful part.
 *   Fixed it     The schedule was wrong and has been corrected. This is the
 *                number that justifies the whole feature.
 *
 * The count of caught misconfigurations is shown deliberately. A monitoring
 * feature that cannot show what it caught is one nobody can argue for keeping.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

interface Anomaly {
  id: string;
  name: string;
  client: string;
  message: string;
  state: 'open' | 'affirmed' | 'fixed';
  firstSeenAt: number;
  lastSeenAt: number;
  resolvedBy: string | null;
  note: string | null;
}

interface Result {
  open: Anomaly[];
  affirmed: Anomaly[];
  caught: number;
  wentAway: Anomaly[];
}

const since = (ms: number) => {
  if (!ms) return 'just now';
  const h = (Date.now() - ms) / 3_600_000;
  if (h < 1) return 'less than an hour';
  if (h < 48) return `${Math.round(h)} hours`;
  return `${Math.round(h / 24)} days`;
};

export function Anomalies({ orgId, canManage }: { orgId: string | null; canManage: boolean }) {
  const [data, setData] = useState<Result | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [showAffirmed, setShowAffirmed] = useState(false);

  async function load() {
    if (!orgId) return;
    try {
      const r = await httpsCallable(functions, 'listAnomalies')({ orgId });
      setData(r.data as Result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load anomalies.');
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [orgId]);

  async function resolve(a: Anomaly, state: 'affirmed' | 'fixed' | 'open') {
    setBusy(a.id);
    setError(null);
    try {
      await httpsCallable(functions, 'resolveAnomaly')({
        orgId, id: a.id, state, note: notes[a.id] ?? '',
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not save.');
    } finally { setBusy(null); }
  }

  if (!data) return null;

  const nothing = data.open.length === 0 && data.wentAway.length === 0;

  return (
    <section className="panel">
      <h2 className="panel-title">
        Schedule review
        {data.caught > 0 && (
          <span className="anom-caught" title="Misconfigurations found and corrected">
            {data.caught} caught
          </span>
        )}
      </h2>
      <p className="panel-note">
        Backup schedules that look out of place next to the rest of the client. Confirming one as
        intentional stops it being raised again.
      </p>

      {error && <div className="banner banner-error">{error}</div>}

      {nothing && <div className="empty">Nothing looks out of place.</div>}

      {data.open.map((a) => (
        <div className="anom" key={a.id}>
          <div className="anom-head">
            <span className="anom-name">{a.name}</span>
            <span className="dim"> · {a.client}</span>
            <span className="anom-age">seen for {since(a.firstSeenAt)}</span>
          </div>
          <div className="anom-msg">{a.message}</div>
          {canManage && (
            <div className="anom-actions">
              <input
                className="finput anom-note"
                placeholder="Why is it like this? (worth writing — nobody will remember in six months)"
                value={notes[a.id] ?? ''}
                onChange={(e) => setNotes({ ...notes, [a.id]: e.target.value })}
              />
              <button className="btn" disabled={busy === a.id} onClick={() => void resolve(a, 'affirmed')}>
                Intentional
              </button>
              <button className="btn btn-primary" disabled={busy === a.id} onClick={() => void resolve(a, 'fixed')}>
                Fixed it
              </button>
            </div>
          )}
        </div>
      ))}

      {/* Shown rather than hidden: "it stopped being reported" is occasionally
          "somebody deleted the backup job", which is not a resolution. */}
      {data.wentAway.length > 0 && (
        <>
          <div className="flbl" style={{ marginTop: 14 }}>No longer reported</div>
          {data.wentAway.map((a) => (
            <div className="anom anom-quiet" key={a.id}>
              <div className="anom-head">
                <span className="anom-name">{a.name}</span>
                <span className="dim"> · {a.client}</span>
                <span className="anom-age">last seen {since(a.lastSeenAt)} ago</span>
              </div>
              <div className="anom-msg">{a.message}</div>
            </div>
          ))}
        </>
      )}

      {data.affirmed.length > 0 && (
        <>
          <button className="btn-link" style={{ marginTop: 12 }} onClick={() => setShowAffirmed(!showAffirmed)}>
            {showAffirmed ? 'Hide' : 'Show'} {data.affirmed.length} confirmed as intentional
          </button>
          {showAffirmed && data.affirmed.map((a) => (
            <div className="anom anom-quiet" key={a.id}>
              <div className="anom-head">
                <span className="anom-name">{a.name}</span>
                <span className="dim"> · {a.client}</span>
              </div>
              <div className="anom-msg">{a.message}</div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                Confirmed by {a.resolvedBy ?? 'someone'}{a.note ? ` — ${a.note}` : ''}
                {canManage && (
                  <button className="btn-link" style={{ marginLeft: 8 }} onClick={() => void resolve(a, 'open')}>
                    reopen
                  </button>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </section>
  );
}
